/**
 * The attention list's floor tags: what the Building view's floor list
 * carries beside a floor's name (twin slice 1: W2B's `tags` slot, fed from
 * the one attention model the Overview frame mounts, W5G). Pure: the frame
 * hands what this returns down to the view (`floor-tags-context.ts`), and
 * `FloorTag.tsx` draws it.
 *
 * A FLOOR IS TAGGED while a card that is UP names it (`item.level`): one
 * still asking someone to act, or saying who will. A card that looks fixed
 * ("Looks fixed, confirming…", A4) or has cleared tags nothing: there is
 * nothing left to go and do on that floor, and a tag pins its row (W2B).
 * A card with no floor -- a site row, a car between readings -- tags
 * none. A card folded under another (a robot waiting on a raised car) is
 * a card of its own on screen, and names its own floor.
 *
 * WHO ACTS. The tag reads the group of the cards on its floor, in the
 * column's own headings: Needs you, Nexsoss, Info. A floor with cards in
 * more than one group reads the most pressing: someone has to go there
 * before Nexsoss's remote fix matters, and either before something only
 * shown. A card Nexsoss has picked up is still Nexsoss's.
 *
 * ONE FLOOR, ONE TAG. The cards may spell a floor two ways (`L9` from a
 * robot, `L09` from elsewhere): they are one floor here (`sameLevel`),
 * keyed by the first spelling seen, so the floor list never has to choose
 * between two tags for one row.
 *
 * Nothing here filters by audience: the model is already this viewer's
 * (no simulated robot for one who may not see it, no ops-only report for
 * a site audience), so its tags are too.
 */
import { sameLevel } from '../../utils/levels';
import { ATTENTION_LABELS } from './card-words';
import type { AttentionGroup, AttentionItem, AttentionModel } from './select';

/** Each tagged floor's group, keyed by floor (the first spelling seen). */
export type FloorTagGroups = ReadonlyMap<string, AttentionGroup>;

/** No floor tagged: the frame's answer while this viewer's flag is off. */
export const NO_FLOOR_TAGS: FloorTagGroups = new Map();

/** A tag's words: the column's group headings, never a code. */
export const FLOOR_TAG_WORDS: Readonly<Record<AttentionGroup, string>> = {
  needs_you: ATTENTION_LABELS.needsYou,
  nexsoss: ATTENTION_LABELS.nexsoss,
  info: ATTENTION_LABELS.info,
};

/** Lower is more pressing. */
const PRESSING: Readonly<Record<AttentionGroup, number>> = { needs_you: 0, nexsoss: 1, info: 2 };

export function floorTagsOf(model: AttentionModel): FloorTagGroups {
  const tags = new Map<string, AttentionGroup>();
  const visit = (item: AttentionItem) => {
    if (item.status === 'active' && item.level) {
      const floor = [...tags.keys()].find((k) => sameLevel(k, item.level)) ?? item.level;
      const was = tags.get(floor);
      if (was === undefined || PRESSING[item.group] < PRESSING[was]) tags.set(floor, item.group);
    }
    for (const { item: under } of item.folded) visit(under);
  };
  for (const list of [model.siteRows, model.needsYou, model.nexsoss, model.pickedUp, model.info]) {
    for (const item of list) visit(item);
  }
  return tags.size === 0 ? NO_FLOOR_TAGS : tags;
}

/** The same floors with the same groups, whatever the map's identity. */
export function sameFloorTags(a: FloorTagGroups, b: FloorTagGroups): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [floor, group] of a) if (b.get(floor) !== group) return false;
  return true;
}
